import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Radar, ShieldAlert, ShieldCheck, Trash2, Download, RefreshCw,
  Server, Database, AlertTriangle, Loader2, ExternalLink, Plus, ChevronRight,
} from 'lucide-react';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';
import { osintAPI, beaconsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const PURPOSES = [
  { value: 'ir_abuse', label: 'Incident Response & Abuse Validation' },
  { value: 'osint_recon', label: 'OSINT Recon' },
  { value: 'pentest_validation', label: 'Authorized Pentest Validation' },
];

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  fetching: { label: 'Fetching', cls: 'bg-electric-blue/15 text-electric-blue border-electric-blue/30' },
  triaging: { label: 'Triaging', cls: 'bg-shinobi-purple/15 text-shinobi-purple border-shinobi-purple/30' },
  completed: { label: 'Completed', cls: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300' },
  failed: { label: 'Failed', cls: 'bg-danger-red/15 text-danger-red border-danger-red/30' },
  blocked: { label: 'Blocked', cls: 'bg-danger-red/15 text-danger-red border-danger-red/30' },
};

const SEVERITY_CLS = {
  critical: 'text-danger-red',
  high: 'text-orange-600',
  medium: 'text-cyber-yellow',
  low: 'text-electric-blue',
  info: 'text-slate-500',
};

const ACTIVE_STATUSES = ['pending', 'fetching', 'triaging'];

function StatusPill({ status }) {
  const b = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${b.cls}`}>
      {ACTIVE_STATUSES.includes(status) && <Loader2 className="w-3 h-3 animate-spin" />}
      {b.label}
    </span>
  );
}

export default function LeakSiteSweep() {
  const { isBlueTeam } = useAuth();

  const [runs, setRuns] = useState([]);
  const [beacons, setBeacons] = useState([]);
  const [selected, setSelected] = useState(null); // detail object
  const [loadingList, setLoadingList] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });

  // form
  const [seedUrl, setSeedUrl] = useState('');
  const [purpose, setPurpose] = useState('ir_abuse');
  const [beaconId, setBeaconId] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [contextText, setContextText] = useState('');
  const [depthLimit, setDepthLimit] = useState(2);
  const [pageCap, setPageCap] = useState(50);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const eligibleBeacons = useMemo(() => {
    // Backend already restricts a Blue Teamer's beacon list to kind=monitor.
    // Prefer internal_scanner (the profile that registers web_fetch).
    const arr = [...beacons];
    arr.sort((a, b) => {
      const score = (x) => (x.profile === 'internal_scanner' || x.profile === 'pentester' ? 0 : 1);
      return score(a) - score(b);
    });
    return arr;
  }, [beacons]);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await osintAPI.listSweeps();
      setRuns(res.data || []);
    } catch (e) {
      setError('Failed to load sweeps.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchBeacons = useCallback(async () => {
    try {
      const res = await beaconsAPI.list();
      setBeacons(res.data.beacons || []);
    } catch (e) {
      setBeacons([]);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
    fetchBeacons();
  }, [fetchRuns, fetchBeacons]);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await osintAPI.getSweep(id);
      setSelected(res.data);
    } catch (e) {
      setError('Failed to load sweep detail.');
    }
  }, []);

  // poll the selected run while it is active
  useEffect(() => {
    if (!selected || !ACTIVE_STATUSES.includes(selected.status)) return undefined;
    const t = setInterval(() => {
      loadDetail(selected.id);
      fetchRuns();
    }, 4000);
    return () => clearInterval(t);
  }, [selected, loadDetail, fetchRuns]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!seedUrl.trim()) { setError('Seed URL is required.'); return; }
    if (!beaconId) { setError('Pick a beacon to fetch through (non-Wasabi egress).'); return; }
    setSubmitting(true);
    try {
      const res = await osintAPI.createSweep({
        seed_url: seedUrl.trim(),
        beacon_id: beaconId,
        case_number: caseNumber.trim() || null,
        purpose,
        context_text: contextText.trim() || null,
        depth_limit: Number(depthLimit),
        page_cap: Number(pageCap),
      });
      setSelected(res.data);
      await fetchRuns();
      if (res.data.intent_verdict !== 'blocked') {
        setSeedUrl(''); setContextText(''); setCaseNumber('');
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to create sweep.');
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    const id = confirmDelete.id;
    try {
      await osintAPI.deleteSweep(id);
      setRuns((prev) => prev.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      setError('Delete failed.');
    } finally {
      setConfirmDelete({ isOpen: false, id: null });
    }
  };

  const doExport = async (id) => {
    try {
      const res = await osintAPI.exportSweep(id);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leak-sweep-${id}-evidence.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Export failed.');
    }
  };

  const doRetriage = async (id) => {
    try {
      const res = await osintAPI.retriageSweep(id);
      setSelected(res.data);
    } catch (e) {
      setError('Retriage failed.');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="OSINT > Leak-Site Sweep"
        subtitle="Harvest *.wasabisys.com references from a leak site through a non-Wasabi beacon. Enumerate-only - never downloads stolen data."
        icon={Radar}
      />

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-danger-red/40 bg-danger-red/10 text-danger-red text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* --- create form --- */}
        <form onSubmit={submit} className="cyber-card p-5 lg:col-span-1 space-y-3 h-fit">
          <div className="flex items-center gap-2 text-shinobi-green font-semibold">
            <Plus className="w-4 h-4" /> New sweep
          </div>

          <label className="block text-xs text-slate-500">Seed URL (leak-site page)</label>
          <input className="terminal-input w-full" placeholder="https://leak-site.example/victims/acme"
            value={seedUrl} onChange={(e) => setSeedUrl(e.target.value)} />

          <label className="block text-xs text-slate-500">Purpose (required)</label>
          <select className="terminal-input w-full" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>

          <label className="block text-xs text-slate-500 flex items-center gap-1">
            <Server className="w-3 h-3" /> Egress beacon (non-Wasabi origin)
          </label>
          <select className="terminal-input w-full" value={beaconId} onChange={(e) => setBeaconId(e.target.value)}>
            <option value="">Select a beacon...</option>
            {eligibleBeacons.map((b) => (
              <option key={b.id} value={b.id}>
                {b.hostname} · {b.profile} · {b.status}
              </option>
            ))}
          </select>
          {isBlueTeam && (
            <p className="text-[11px] text-slate-500">Use an internal_scanner beacon - it is the monitor profile that runs web fetches.</p>
          )}

          <label className="block text-xs text-slate-500">Case number (optional)</label>
          <input className="terminal-input w-full" placeholder="00294995"
            value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} />

          <label className="block text-xs text-slate-500">Describe the situation (optional)</label>
          <textarea className="terminal-input w-full h-24 resize-y"
            placeholder="Reference context only - sharpens AI triage. Never an action channel. Off-purpose descriptions are hard-blocked."
            value={contextText} onChange={(e) => setContextText(e.target.value)} />

          <button type="button" className="text-xs text-slate-500 hover:text-slate-800"
            onClick={() => setShowAdvanced((s) => !s)}>
            {showAdvanced ? 'Hide' : 'Show'} advanced (depth / page cap)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-500">Crawl depth (0-4)</label>
                <input type="number" min={0} max={4} className="terminal-input w-full"
                  value={depthLimit} onChange={(e) => setDepthLimit(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">Page cap (1-200)</label>
                <input type="number" min={1} max={200} className="terminal-input w-full"
                  value={pageCap} onChange={(e) => setPageCap(e.target.value)} />
              </div>
            </div>
          )}

          <button type="submit" disabled={submitting}
            className="btn-cyber w-full flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
            Launch sweep
          </button>
        </form>

        {/* --- runs list + detail --- */}
        <div className="lg:col-span-2 space-y-4">
          {/* list */}
          <div className="cyber-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-700 font-semibold">Recent sweeps</span>
              <button onClick={fetchRuns} className="text-slate-500 hover:text-slate-800">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {loadingList ? (
              <div className="text-slate-500 text-sm py-4">Loading...</div>
            ) : runs.length === 0 ? (
              <div className="text-slate-500 text-sm py-4">No sweeps yet. Launch one on the left.</div>
            ) : (
              <div className="divide-y divide-cyber-300/30">
                {runs.map((r) => (
                  <button key={r.id} onClick={() => loadDetail(r.id)}
                    className={`w-full text-left py-2.5 px-2 flex items-center gap-3 hover:bg-cyber-200/40 rounded ${selected?.id === r.id ? 'bg-cyber-200/50' : ''}`}>
                    <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="flex-1 truncate text-sm text-slate-800">{r.seed_host}</span>
                    {r.case_number && <span className="text-[11px] text-slate-500">#{r.case_number}</span>}
                    <span className="text-[11px] text-slate-500">{r.refs_found} refs</span>
                    <StatusPill status={r.status} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* detail */}
          {selected && <SweepDetail run={selected}
            onDelete={(id) => setConfirmDelete({ isOpen: true, id })}
            onExport={doExport} onRetriage={doRetriage} />}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={doDelete}
        title="Delete sweep"
        message="Delete this sweep and all its captured evidence? This cannot be undone."
        confirmText="Delete sweep"
        variant="danger"
      />
    </div>
  );
}

function SweepDetail({ run, onDelete, onExport, onRetriage }) {
  const blocked = run.intent_verdict === 'blocked';
  return (
    <div className="cyber-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusPill status={run.status} />
            {run.case_number && <span className="text-xs text-slate-500">Case #{run.case_number}</span>}
          </div>
          <a href={run.seed_url} target="_blank" rel="noreferrer"
            className="text-sm text-electric-blue hover:underline break-all flex items-center gap-1 mt-1">
            {run.seed_url} <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
          <div className="text-[11px] text-slate-500 mt-1">
            {run.pages_fetched} pages · {run.refs_found} refs · depth {run.depth_limit} · cap {run.page_cap}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!blocked && (
            <>
              <button onClick={() => onRetriage(run.id)} title="Re-run AI triage"
                className="p-2 rounded hover:bg-cyber-200/50 text-slate-700"><RefreshCw className="w-4 h-4" /></button>
              <button onClick={() => onExport(run.id)} title="Export evidence pack"
                className="p-2 rounded hover:bg-cyber-200/50 text-shinobi-green"><Download className="w-4 h-4" /></button>
            </>
          )}
          <button onClick={() => onDelete(run.id)} title="Delete sweep"
            className="p-2 rounded hover:bg-cyber-200/50 text-danger-red"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* intent verdict banner */}
      {blocked ? (
        <div className="px-4 py-3 rounded-lg border border-danger-red/40 bg-danger-red/10 text-sm">
          <div className="flex items-center gap-2 text-danger-red font-semibold"><ShieldAlert className="w-4 h-4" /> Blocked by intent gate</div>
          <p className="text-slate-700 mt-1">{run.intent_reason}</p>
          <p className="text-[11px] text-slate-500 mt-1">The free-text description was found out of scope for the declared purpose. Rewrite it (or clear it) and launch again.</p>
        </div>
      ) : run.intent_reason ? (
        <div className="px-3 py-2 rounded-lg border border-wasabi-300 bg-shinobi-green/5 text-xs flex items-center gap-2 text-slate-700">
          <ShieldCheck className="w-4 h-4 text-shinobi-green shrink-0" /> {run.intent_reason}
        </div>
      ) : null}

      {/* suggested urls (click-to-add is a future nicety; shown for awareness) */}
      {run.suggested_urls?.length > 0 && (
        <div className="text-[11px] text-slate-500">
          URLs mentioned in your notes (not fetched): {run.suggested_urls.join(', ')}
        </div>
      )}

      {/* refs table */}
      {!blocked && (
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold mb-2">
            <Database className="w-4 h-4 text-shinobi-green" /> Candidate Wasabi buckets ({run.refs?.length || 0})
          </div>
          {(!run.refs || run.refs.length === 0) ? (
            <div className="text-slate-500 text-sm py-3">
              {ACTIVE_STATUSES.includes(run.status) ? 'Sweep in progress...' : 'No *.wasabisys.com references found on the fetched pages.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] text-slate-500 border-b border-cyber-300/30">
                    <th className="py-1.5 pr-3">Bucket</th>
                    <th className="py-1.5 pr-3">Region</th>
                    <th className="py-1.5 pr-3">Style</th>
                    <th className="py-1.5 pr-3">Victim (AI)</th>
                    <th className="py-1.5 pr-3">Sev</th>
                    <th className="py-1.5 pr-3">Conf</th>
                  </tr>
                </thead>
                <tbody>
                  {run.refs.map((r) => (
                    <tr key={r.id} className="border-b border-cyber-300/20 align-top">
                      <td className="py-2 pr-3 font-mono text-slate-800 break-all">{r.bucket || <span className="text-slate-500">(path-style)</span>}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.region || '?'}</td>
                      <td className="py-2 pr-3 text-slate-500">{r.url_style}</td>
                      <td className="py-2 pr-3 text-slate-800">
                        {r.ai_victim_org || <span className="text-slate-500">-</span>}
                        {r.ai_is_wasabi === false && <span className="ml-1 text-[10px] text-orange-600">not-wasabi?</span>}
                        {r.ai_reason && <div className="text-[11px] text-slate-500">{r.ai_reason}</div>}
                      </td>
                      <td className={`py-2 pr-3 ${SEVERITY_CLS[r.ai_severity] || 'text-slate-500'}`}>{r.ai_severity || '-'}</td>
                      <td className="py-2 pr-3 text-slate-500">{r.ai_confidence != null ? `${Math.round(r.ai_confidence * 100)}%` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* evidence (pages) */}
      {run.pages?.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-800">Evidence: {run.pages.length} fetched pages</summary>
          <div className="mt-2 space-y-1">
            {run.pages.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                <span className={p.http_status === 200 ? 'text-shinobi-green' : 'text-orange-600'}>{p.http_status || 'ERR'}</span>
                <span className="truncate flex-1 text-slate-500">{p.url}</span>
                <span>{p.byte_len}b{p.truncated ? '+' : ''}</span>
                {p.sha256 && <span title={p.sha256}>{p.sha256.slice(0, 10)}</span>}
                {p.fetch_error && <span className="text-danger-red" title={p.fetch_error}>err</span>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
